import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { ArrowLeft } from "lucide-react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useRoom } from "../context/RoomContext";
import { useHotel } from "../context/HotelContext";
import LoadingGif from "../components/LoadingGif";
import { useState, useEffect } from "react";

const YES_NO_QUESTIONS = [
  { field: "heating24h", labelKey: "Heating24hLabel", heating24hfeeField: "heatingFee" },
  { field: "petPhotosVideos", labelKey: "PetPhotosVideosLabel" },
  { field: "customSnacks", labelKey: "CustomSnacksLabel" },
  { field: "ventilation24h", labelKey: "Ventilation24hLabel" },
  { field: "dailyDisinfection", labelKey: "DailyDisinfectionLabel" },
  { field: "dailyInteraction", labelKey: "DailyInteractionLabel" },
  { field: "customRequests", labelKey: "CustomRequestsLabel" },
  { field: "hasWindow", labelKey: "HasWindowLabel" },
  { field: "lateCheckout", labelKey: "LateCheckoutLabel", lateCheckoutfeeField: "lateCheckoutFee" },
];

const FREE_CANCEL_OPTIONS = [
  { value: "14days", labelKey: "CancelDeadline14" },
  { value: "7days", labelKey: "CancelDeadline7" },
  { value: "3days", labelKey: "CancelDeadline3" },
  { value: "24hours", labelKey: "CancelDeadline24h" },
  { value: "none", labelKey: "CancelDeadlineNone" },
];

const PET_TYPE_OPTIONS = [
  { key: "dog", labelKey: "AnimalDog" },
  { key: "cat", labelKey: "AnimalCat" },
  { key: "rabbit", labelKey: "AnimalRabbit" },
  { key: "reptile", labelKey: "PetTypeReptile" },
  { key: "smallAnimal", labelKey: "PetTypeSmallAnimal" },
];

const LATE_FEE_OPTIONS = [
  { value: "firstNight", labelKey: "LateFeeFirstNight" },
  { value: "fullAmount", labelKey: "LateFeeFullAmount" },
  { value: "fixed", labelKey: "LateFeeFixed" },
  { value: "nonRefundable", labelKey: "LateFeeNonRefundable" },
];

const HotelAdd_YOverview = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hotelId, roomId } = useParams();
  const isMobile = useMediaQuery("(max-width:950px)");
  const { fetchRoom, roomData, loading, updateRoomStatus } = useRoom();
  const { fetchDocuments, fetchDraft } = useHotel();
  const [roomPhotos, setRoomPhotos] = useState([]);
  const [photoUrls, setPhotoUrls] = useState([]);
  const [searchParams] = useSearchParams();
  const manage = searchParams.get('manage') || '';
  const view = searchParams.get('view') || '';
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [roomName, setRoomName] = useState("");

  useEffect(() => {
    if (roomId) {
      fetchRoom(roomId);
    }
  }, [roomId, fetchRoom]);

  useEffect(() => {
    if (hotelId) {
      fetchDraft(hotelId);
    }
  }, [hotelId, fetchDraft]);

  const handleNext = () => {
    try {
      if (manage) {
        setShowPublishModal(true);
      } else {
        navigate(`/hotel/add/room-info-add-1/${hotelId}`);
      }
    } catch (err) {
      console.error("Error navigating to next step:", err);
    }
  };

  const handlePublish = async () => {
    try {
      await updateRoomStatus(roomId, "published");
      setShowPublishModal(false);
      navigate(`/hotel/room-management/${hotelId}`);
    } catch (err) {
      console.error("Error publishing room:", err);
    }
  }

  useEffect(() => {
    if (hotelId && roomId) {
      fetchDocuments().then((response) => {
        const documents = response.data?.data || [];
        const roomPhotos = documents.filter((doc) => doc.type === "roomPhoto" && doc.roomId === roomId);

        if (roomPhotos.length > 0) {
          const photoUrls = roomPhotos.map((doc) => doc.url);
          setPhotoUrls(photoUrls);

          // Create file-like objects for display
          const fileObjs = roomPhotos.map((doc) => ({
            name: doc.fileName || `photo-${doc.documentId}`,
            url: doc.url,
            documentId: doc.documentId
          }));
          setRoomPhotos(fileObjs);
        }
      }).catch((err) => console.error("Failed to fetch room photos:", err));
    }
  }, [hotelId, roomId, fetchDocuments]);

  useEffect(() => {
    setRoomName(roomData?.information1?.roomName || "");
  }, [roomData]);

  useEffect(() => {
    const urls = roomPhotos.map((file) => {
      if (file instanceof File) {
        return URL.createObjectURL(file);
      }
      return file.url || null;
    });
    setPhotoUrls(urls);
    return () => {
      urls.forEach((url) => {
        if (url && typeof url === "string" && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [roomPhotos]);

  const rowLabelSx = { fontSize: isMobile ? "12px" : "13px", color: "#999", mb: 0.5 };
  const rowValueSx = { fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" };

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  const EditLink1 = () => (
    <Button
      onClick={() => navigate(`/hotel/${hotelId}/room-info-1/${roomId}`)}
      sx={{
        alignSelf: "flex-start",
        color: "#31BACD",
        textTransform: "none",
        fontSize: isMobile ? "13px" : "14px",
        textDecoration: "underline",
        minWidth: "auto",
        padding: 0,
        mt: 0.5,
      }}
    >
      {t('Edit')}
    </Button>
  );
  const EditLink2 = () => (
    <Button
      onClick={() => navigate(`/hotel/${hotelId}/room-info-2/${roomId}`)}
      sx={{
        alignSelf: "flex-start",
        color: "#31BACD",
        textTransform: "none",
        fontSize: isMobile ? "13px" : "14px",
        textDecoration: "underline",
        minWidth: "auto",
        padding: 0,
        mt: 0.5,
      }}
    >
      {t('Edit')}
    </Button>
  );
  const EditLink3 = () => (
    <Button
      onClick={() => navigate(`/hotel/${hotelId}/room-info-3/${roomId}`)}
      sx={{
        alignSelf: "flex-start",
        color: "#31BACD",
        textTransform: "none",
        fontSize: isMobile ? "13px" : "14px",
        textDecoration: "underline",
        minWidth: "auto",
        padding: 0,
        mt: 0.5,
      }}
    >
      {t('Edit')}
    </Button>
  );

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>

        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
          {/* Heading */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <Button
              onClick={() => navigate(-1)}
              sx={{
                minWidth: "auto",
                width: isMobile ? "36px" : "44px",
                height: isMobile ? "36px" : "44px",
                borderRadius: "50%",
                backgroundColor: "#31BACD",
                color: "#fff",
                padding: 0,
                "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
              }}
            >
              <ArrowLeft size={isMobile ? 18 : 22} />
            </Button>
            <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000" }}>
              {manage ? t('RoomManagementTitle') + t('AddaNewRoomTitle') : view ? t('RoomManagementTitle') + t('ViewRoom') + roomName : t('ReviewInfoTitle')}
            </Typography> 
          </Box>

          {loading ? (
            <LoadingGif />
          ) : (
            <Box sx={{ width: "100%" }}>
              {roomData?.information1 && (
                <>
                  <Box sx={{ display: "flex", gap: 3, padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={rowLabelSx}>{t('RoomNameLabel')}</Typography>
                      <Typography sx={rowValueSx}>{roomData.information1.roomName}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={rowLabelSx}>{t('RoomQuantityLabel')}</Typography>
                      <Typography sx={rowValueSx}>{roomData.information1.roomQuantity}</Typography>
                    </Box>
                    <EditLink1 />
                  </Box>

                  <Box sx={{ display: "flex", gap: 3, padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={rowLabelSx}>{t('AcceptedPetTypeLabel')}</Typography>
                      <Typography sx={rowValueSx}>{roomData.information1.petTypes.map((petType) => t(PET_TYPE_OPTIONS.find((option) => option.key === petType)?.labelKey || "")).join(", ")}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={rowLabelSx}>{t('AcceptedPetQuantityLabel')}</Typography>
                      <Typography sx={rowValueSx}>{roomData.information1.petQuantity}</Typography>
                    </Box>
                    <EditLink1 />
                  </Box>

                  <Box sx={{ display: "flex", gap: 3, padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={rowLabelSx}>{t('RoomDimensionsLabel')}</Typography>
                      <Typography sx={rowValueSx}>
                        {roomData.information1.dimensions?.length || " - "} X {roomData.information1.dimensions?.width || " - "} X {roomData.information1.dimensions?.height || " - "}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={rowLabelSx}>{t('RoomDescLabel')}</Typography>
                      <Typography sx={rowValueSx}>{roomData.information1.description}</Typography>
                    </Box>
                    <EditLink1 />
                  </Box>

                  <Box sx={{ padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                    <Typography sx={rowLabelSx}>{t('RoomPhotosLabel')}</Typography>
                    <Box sx={{ display: "flex", gap: 1.5, mb: 0.5 }}>
                      {roomPhotos.map((file, index) => (
                        <Box
                          key={`${file.name}-${index}`}
                          sx={{
                            position: "relative",
                            width: isMobile ? "80px" : "100px",
                            height: isMobile ? "80px" : "100px",
                            borderRadius: "10px",
                            overflow: "hidden",
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            component="img"
                            src={photoUrls[index]}
                            alt={file.name}
                            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                          />
                        </Box>
                      ))}
                    </Box>
                    <EditLink1 />
                  </Box>

                  <Box sx={{ display: "flex", gap: 3, padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={rowLabelSx}>{t('WeekdayPriceLabel')}</Typography>
                      <Typography sx={rowValueSx}>{roomData.information1.weekdayPrice}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={rowLabelSx}>{t('WeekendPriceLabel')}</Typography>
                      <Typography sx={rowValueSx}>{roomData.information1.weekendPrice}</Typography>
                    </Box>
                    <EditLink1 />
                  </Box>

                  <Box sx={{ display: "flex", gap: 3, padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={rowLabelSx}>{t('FreeCancelDeadlineLabel')}</Typography>
                      <Typography sx={rowValueSx}>{FREE_CANCEL_OPTIONS.map((option) => (option.value === roomData.information1.freeCancelDeadline ? t(option.labelKey) : null)).filter((v) => v !== null).join(", ")}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={rowLabelSx}>{t('LateCancelFeeLabel')}</Typography>
                      <Typography sx={rowValueSx}>{LATE_FEE_OPTIONS.map((option) => (option.value === roomData.information1.lateCancelFee ? t(option.labelKey) : null)).filter((v) => v !== null).join(", ")}</Typography>
                    </Box>
                    <EditLink1 />
                  </Box>

                  <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 0, borderBottom: "1px solid #E0E0E0", }}>
                    {YES_NO_QUESTIONS.map((question) => (
                      <Box key={question.field} sx={{
                        padding: "16px 0",
                      }}>
                        <Typography sx={rowLabelSx}>{t(question.labelKey)}</Typography>
                        <Typography sx={rowValueSx}>{roomData.information2?.[question.field] ? t('Yes') : t('No')}</Typography>
                        <EditLink2 />
                      </Box>
                    ))}
                  </Box>
                </>
              )}

              {roomData?.information3 && (
                <Box sx={{ padding: "16px 0" }}>
                  <Typography sx={rowLabelSx}>{t('PromotionQuestionLabel')}</Typography>
                  {roomData.information3.promotionChoice === "earlyBird" && (
                    <Typography sx={rowValueSx}>{roomData.information3.earlyBirdDiscount}{t('DiscountLabel')} {t('EarlyBirdPromotionLabel1')} {roomData.information3.earlyBirdDiscountDays} {t('EarlyBirdPromotionLabel2')}</Typography>
                  )}
                  {roomData.information3.promotionChoice === "addon" && (
                    <Typography sx={rowValueSx}>{roomData.information3.addonDiscount}{t('DiscountLabel')} {t('AddonPromotionLabel')}</Typography>
                  )}
                  {roomData.information3.promotionChoice === "custom" && (
                    <Typography sx={rowValueSx}>{roomData.information3.customPromotionsDiscount}{t('DiscountLabel')} {roomData.information3.customPromotionsLabel}</Typography>
                  )}
                  <EditLink3 />
                </Box>
              )}
            </Box>
          )}
          {!view && (
            <>
              <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#999", mt: 3 }}>
                {t('ConfirmBeforeSubmitNote')}
              </Typography>

              {/* Bottom actions */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  mt: 4,
                }}
              >
                <Button
                  onClick={() => navigate(manage ? `/hotel/room-management/${hotelId}` : "/hotel/onboarding")}
                  sx={{
                    color: "#666",
                    textTransform: "none",
                    fontSize: isMobile ? "14px" : "16px",
                    textDecoration: "underline",
                  }}
                >
                  {t('SaveAndLeave')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{
                    backgroundColor: "#31BACD",
                    color: "#fff",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontSize: isMobile ? "14px" : "16px",
                    padding: "10px 32px",
                    "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  }}
                >
                  {manage ? t('Submit') : t('NextStep')}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </div>
      <Footer />

      {showPublishModal && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 2 : 4,
              backgroundColor: "#fff",
              borderRadius: "16px",
              padding: "24px",
              width: isMobile ? "80%" : "auto",
              maxWidth: "700px",
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <Box sx={{ width: isMobile ? "100%" : "320px", flexShrink: 0, }}>
              <Box
                component="img"
                src="/icons/ppclogo.png"
                alt="Pet Pet Club"
                sx={{ height: isMobile ? "20px" : "24px", mb: 2 }}
              />
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, mb: 2 }}>
                <Typography sx={{ fontSize: isMobile ? "16px" : "14px", color: "#000", fontWeight: "700" }}>
                  {t('PublishRoomTitle')}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "16px" : "14px", color: "#666", }}>
                  {t('PublishRoomDesc')}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, width: "80%", }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => setShowPublishModal(false)}
                    sx={{
                      backgroundColor: "#D9D9D9",
                      color: "#fff",
                      borderRadius: "10px",
                      textTransform: "none",
                      fontSize: isMobile ? "13px" : "14px",
                      padding: "8px 16px",
                      "&:hover": { backgroundColor: "#e6e6e6", },
                    }}
                  >
                    {t('Back')}
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handlePublish}
                    sx={{
                      backgroundColor: "#00A63E",
                      color: "#fff",
                      borderRadius: "10px",
                      textTransform: "none",
                      fontSize: isMobile ? "13px" : "14px",
                      padding: "8px 16px",
                      "&:hover": { backgroundColor: "#6ad391", },
                    }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : t('Publish')}
                  </Button>
                </Box>

              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default HotelAdd_YOverview;
